/**
 * 8 Super-Category cards in a responsive grid (matches Infirmly mockup).
 * Accessibility: WCAG 2.2 AA compliant — high-contrast labels, keyboard focus,
 * min 44×44 tap targets, aria labels.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  HeartPulse, Briefcase, Scale, Home, UserCircle, PiggyBank,
  GraduationCap, MoreHorizontal,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  HeartPulse, Briefcase, Scale, Home, UserCircle, PiggyBank,
  GraduationCap, MoreHorizontal,
};

export const SuperCategories = ({ language = "en", onSelect }) => {
  const [cats, setCats] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/super-categories`)
      .then(r => setCats(r.data.super_categories || []))
      .catch(() => {});
  }, []);

  const handle = (cat) => {
    if (onSelect) onSelect(cat);
    else navigate(`/register?category=${cat.id}`);
  };

  const servicesLabel = language === "de" ? "Dienste"
    : language === "fr" ? "services"
    : language === "it" ? "servizi" : "services";

  return (
    <section aria-labelledby="super-cat-heading" data-testid="super-categories">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2
          id="super-cat-heading"
          className="text-lg font-bold text-white"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {language === "de" ? "Kategorien durchsuchen"
            : language === "fr" ? "Parcourir les catégories"
            : language === "it" ? "Sfoglia categorie" : "Browse Categories"}
        </h2>
        <button
          onClick={() => navigate("/categories")}
          className="text-[#93c5fd] hover:text-white text-sm font-semibold transition-colors underline-offset-4 hover:underline min-h-[44px] px-2 -mr-2 rounded"
          aria-label={language === "de" ? "Alle Kategorien ansehen" : "View all categories"}
        >
          {language === "de" ? "Alle ansehen" : language === "fr" ? "Voir tout" : language === "it" ? "Vedi tutto" : "View all"}
        </button>
      </div>

      <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3 list-none p-0 m-0" role="list">
        {cats.map(cat => {
          const Icon = ICONS[cat.icon] || Briefcase;
          const name = cat[`name_${language}`] || (language === "de" ? cat.name : null) || cat.name_en || cat.name;
          const count = cat.members?.length || 0;
          return (
            <li key={cat.id}>
              <button
                onClick={() => handle(cat)}
                data-testid={`super-cat-${cat.id}`}
                aria-label={`${name}, ${count} ${servicesLabel}`}
                className="group relative w-full h-full min-h-[112px] bg-white/[0.05] hover:bg-white/[0.10] focus-visible:bg-white/[0.10] border border-white/15 hover:border-[#60a5fa]/60 rounded-2xl p-4 transition-all duration-200 text-left backdrop-blur-sm"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0044CC] to-[#1d4ed8] flex items-center justify-center mb-3 group-hover:scale-105 transition-transform" aria-hidden="true">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div className="text-white font-semibold text-sm leading-tight">{name}</div>
                <div className="text-hi-contrast-sub text-xs mt-1">
                  {count} {servicesLabel}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SuperCategories;
